import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { MarcoFichaje } from '@/components/MarcoFichaje';
import {
  accionesPosibles, duracionDesde, fechaCorta, horaLocal, horasMinutos,
} from '@/lib/fichajes';

/**
 * Fichar. La hora la pone SIEMPRE el servidor: aquí no se escribe ninguna.
 * Sustituye a la pantalla de PIN anterior (/time-clock), que dejaba fichar por
 * otro sin identificarse.
 */

const HECHO = {
  entrada: 'Entrada registrada',
  salida: 'Salida registrada',
  pausa_inicio: 'Pausa iniciada',
  pausa_fin: 'Pausa terminada',
  olvido_salida: 'Anotado. Ya puedes fichar la entrada de hoy',
};

function fechaLarga(zona) {
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: zona, weekday: 'long', day: 'numeric', month: 'long',
  }).format(new Date());
}

function LineaJornada({ jornada, zona }) {
  const filas = [{ hora: horaLocal(jornada.entrada, zona), texto: 'Entrada' }];
  jornada.pausas.forEach((p) => {
    filas.push({ hora: horaLocal(p.inicio, zona), texto: `Pausa hasta las ${horaLocal(p.fin, zona)}` });
  });
  if (jornada.pausa_abierta) filas.push({ hora: horaLocal(jornada.pausa_desde, zona), texto: 'En pausa…' });
  if (jornada.salida) {
    filas.push({ hora: horaLocal(jornada.salida, zona), texto: `Salida · ${horasMinutos(jornada.minutos_trabajados)} h` });
  } else if (jornada.olvido_salida) {
    filas.push({ hora: '', texto: 'Salida olvidada: pendiente de corregir' });
  } else if (!jornada.pausa_abierta) {
    filas.push({ hora: '', texto: 'En curso…' });
  }
  return (
    <ol className="border-l-2 border-slate-200 ml-1 pl-4 space-y-1.5">
      {filas.map((f, i) => (
        <li key={i} className="text-sm text-slate-700">
          <span className="font-mono tabular-nums text-slate-900 mr-2">{f.hora}</span>
          {f.texto}
        </li>
      ))}
    </ol>
  );
}

export default function Fichar({ user }) {
  const [situacion, setSituacion] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [ahora, setAhora] = useState(new Date());
  const [confirmarOlvido, setConfirmarOlvido] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/fichajes/hoy');
      setSituacion(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido cargar tu jornada');
    }
  }, []);

  useEffect(() => {
    cargar();
    const reloj = setInterval(() => setAhora(new Date()), 30000);
    const refresco = setInterval(cargar, 120000);
    return () => { clearInterval(reloj); clearInterval(refresco); };
  }, [cargar]);

  const fichar = async (tipo) => {
    if (enviando) return;
    setEnviando(true);
    try {
      const r = await axiosInstance.post('/fichajes/eventos', { tipo });
      setSituacion((antes) => ({ ...antes, ...r.data.situacion }));
      setAhora(new Date());
      toast.success(HECHO[tipo] || 'Registrado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido fichar. Vuelve a intentarlo.');
      cargar();
    } finally {
      setEnviando(false);
      setConfirmarOlvido(false);
    }
  };

  const aceptarAviso = async () => {
    try {
      await axiosInstance.post('/fichajes/aviso-leido');
      setSituacion((antes) => ({ ...antes, aviso_leido: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido guardar');
    }
  };

  if (!situacion) {
    return <MarcoFichaje user={user}><p className="text-slate-500">Cargando tu jornada…</p></MarcoFichaje>;
  }

  const zona = situacion.config?.zona_horaria || 'Europe/Madrid';
  const acciones = accionesPosibles(situacion);
  const bloqueado = enviando || !situacion.aviso_leido;
  const jornada = situacion.jornada;

  // La jornada en curso puede ser de ayer (turno de noche): se enseña junto a
  // las de hoy, sin repetirla.
  const jornadas = [...(situacion.jornadas_hoy || [])];
  if (jornada && !jornadas.some((j) => j.jornada_id === jornada.jornada_id)) jornadas.unshift(jornada);

  return (
    <MarcoFichaje user={user}>
      <div className="space-y-5">
        <div>
          <p className="text-sm text-slate-500 first-letter:uppercase">{fechaLarga(zona)}</p>
          <h1 className="text-2xl font-bold text-slate-900">
            {situacion.en_pausa
              ? `En pausa desde las ${horaLocal(jornada.pausa_desde, zona)}`
              : situacion.en_jornada
                ? `En jornada desde las ${horaLocal(jornada.entrada, zona)}`
                : 'Fuera de jornada'}
          </h1>
          {situacion.en_jornada && (
            <p className="text-slate-600 mt-1">Llevas {duracionDesde(jornada.entrada, ahora)}</p>
          )}
        </div>

        {!situacion.aviso_leido && (
          <Card className="border-slate-300">
            <CardContent className="pt-5 space-y-3 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">Antes de fichar por primera vez</p>
              <p>
                Al fichar se guarda la hora (la pone el servidor, no tu móvil), la dirección IP
                y el navegador desde el que fichas. No se guarda tu ubicación.
              </p>
              <p>
                Es el registro diario de jornada que la empresa está obligada a llevar (art. 34.9
                del Estatuto de los Trabajadores). Se conserva cuatro años. Lo ves tú en «Mis
                registros», lo ve la empresa y, si lo piden, tus representantes y la Inspección
                de Trabajo. Cada mes te llega por email.
              </p>
              <Button onClick={aceptarAviso} className="w-full">Entendido</Button>
            </CardContent>
          </Card>
        )}

        {situacion.olvidada && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Tu jornada del {fechaCorta(situacion.olvidada.fecha_local)} (entrada a las{' '}
            {horaLocal(situacion.olvidada.entrada, zona)}) se quedó sin salida. Ficha la entrada de
            hoy con normalidad y pide a tu responsable que apunte la hora real de salida.
          </div>
        )}

        <div className="space-y-2">
          <Button
            onClick={() => fichar(acciones.principal.tipo)}
            disabled={bloqueado}
            className="w-full h-16 text-xl font-bold"
          >
            {enviando ? 'Registrando…' : acciones.principal.etiqueta}
          </Button>
          {acciones.secundarias.map((a) => (
            <Button
              key={a.tipo}
              variant={a.tipo === 'olvido_salida' ? 'ghost' : 'outline'}
              disabled={bloqueado}
              onClick={() => (a.tipo === 'olvido_salida' ? setConfirmarOlvido(true) : fichar(a.tipo))}
              className={`w-full ${a.tipo === 'olvido_salida' ? 'text-slate-500 underline' : 'h-12'}`}
            >
              {a.etiqueta}
            </Button>
          ))}
        </div>

        {jornadas.length > 0 && (
          <Card>
            <CardContent className="pt-5 space-y-4">
              {jornadas.map((j) => <LineaJornada key={j.jornada_id} jornada={j} zona={zona} />)}
            </CardContent>
          </Card>
        )}

        <div className="text-xs text-slate-500 space-y-2">
          {situacion.config?.texto_desplazamientos && <p>{situacion.config.texto_desplazamientos}</p>}
          <p>
            ¿Te equivocaste o se te olvidó fichar?{' '}
            <Link to="/mis-registros" className="underline text-slate-700">Pide la corrección en «Mis registros»</Link>.
            Tu responsable la revisa, lo de antes no se borra y te llega un email con la respuesta.
          </p>
        </div>
      </div>

      <Dialog open={confirmarOlvido} onOpenChange={setConfirmarOlvido}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Se te olvidó fichar la salida?</DialogTitle>
            <DialogDescription>
              Tu jornada abierta se cerrará sin hora de salida y tu responsable recibirá un aviso
              para apuntar la hora real. Después podrás fichar la entrada de hoy con su hora de
              verdad.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmarOlvido(false)}>Cancelar</Button>
            <Button onClick={() => fichar('olvido_salida')} disabled={enviando}>Sí, se me olvidó</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MarcoFichaje>
  );
}
